import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, CreditCard, Upload, X, Calendar, Download, Filter, Images, Settings } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import CSVExportButton from '../components/CSVExportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default categories - can be extended by user
const DEFAULT_EXPENSE_CATEGORIES = ['Staff Cost', 'Advertising', 'General & Admin Expenses'];

// IB Channels for Advertising sub-category
const IB_CHANNELS = [
  'Fira', 'MBE', 'VT Markets', 'XM', 'Exness', 'IC Markets', 'FBS',
  // New Channels
  'MSE', 'USTAD PADEI', 'FAIZ ISMAIL', 'EIZLAN YUSOFF', 'CIKO SUPER SCALPER', 'GARUDA', 'REVOLUSI NUSANTARA',
  'Other'
];

const DEFAULT_SUB_CATEGORIES = {
  'Staff Cost': [
    'Salary', 'EPF', 'SOCSO', 'EIS', 'Bonus', 'Allowance', 'Overtime',
    // New sub-categories from request
    'PCB Staff', 'Commission Staff', 'Accommodation Staff', 'Reward Staff',
    'Director Allowance', 'Director Fee', 'Director Accommodation',
    'Other'
  ],
  'Advertising': [
    // New sub-categories from request
    'Underads', 'Garuda', 'MBE', 'Ustad Faiz Ismail', 'Padei', 'Neurx',
    'Revolusi Nusantara', 'Sahara AI', 'Ciko Superscalper', 'Shooting Expenses', 'Service',
    // Legacy items
    ...IB_CHANNELS.filter(ch => ch !== 'Other').map(ch => `${ch} Ads`),
    'Google Ads', 'Facebook Ads', 'TikTok Ads', 'Influencer', 'Other'
  ],
  'General & Admin Expenses': [
    // New sub-categories from request
    'Parking', 'Upkeep', 'Office Equipment', 'Staff Training', 'Office Rental',
    'Postage & Courier', 'Office Refreshment', 'Secretarial Fee', 'Subscription',
    'Meal Expense', 'Repair & Maintenance', 'Rental Expense', 'Bank Charge',
    'Podcast Expenses', 'Petrol', 'Toll', 'Medical Expense',
    'Interest Expense - Maybank Islamic Financing', 'Interest Expense - Maybank Conventional Loan',
    'Depreciation of Fixed Assets',
    // Legacy items
    'Rent', 'Utilities', 'Tools/Software', 'Office', 'Travel', 'Entertainment', 'Professional Fees', 'Other'
  ]
};

const PAYMENT_FROM = ['Main Account', 'Marketing Budget', 'Operations Fund', 'Petty Cash'];
const COLORS = ['#C41E3A', '#DC143C', '#B22222', '#8B0000', '#A52A2A', '#CD5C5C', '#E9967A', '#F08080'];

const formatRM = (amount) => `RM ${(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getTodayDate = () => new Date().toISOString().split('T')[0];
const getWeekStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().split('T')[0];
};
const getWeekEnd = () => {
  const d = new Date();
  d.setDate(d.getDate() + (6 - d.getDay()));
  return d.toISOString().split('T')[0];
};
const getMonthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

// Bulk Upload Component
const BulkUploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = (newFiles) => {
    const validTypes = ['.jpg', '.jpeg', '.png', '.pdf'];
    const validFiles = newFiles.filter(file => {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      return validTypes.includes(ext);
    });
    setFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const handleFilesSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and Drop Handlers
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await axios.post(`${API_URL}/api/upload/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      const urls = response.data.uploaded.map(f => ({ name: f.original_name, url: f.url }));
      setUploadedUrls(urls);
      
      if (response.data.errors && response.data.errors.length > 0) {
        setError(`${response.data.total_errors} file(s) failed to upload`);
      }
      
      if (urls.length > 0) {
        onUploadComplete(urls);
      }
    } catch (err) {
      console.error('Bulk upload failed:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadedUrls([]);
    setError(null);
    setUploadProgress(0);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Bulk Upload Images">
      <div className="space-y-4">
        <div 
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
            isDragging ? 'border-red-500 bg-red-50 scale-[1.02]' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <label className="cursor-pointer">
            <input type="file" accept=".jpg,.jpeg,.png,.pdf" multiple onChange={handleFilesSelect} className="hidden" />
            <Images className={`w-12 h-12 mx-auto mb-2 transition-colors ${isDragging ? 'text-red-500' : 'text-gray-400'}`} />
            <p className={`text-sm font-medium ${isDragging ? 'text-red-600' : 'text-gray-600'}`}>
              {isDragging ? 'Drop files here!' : 'Drag & drop files here'}
            </p>
            <p className="text-xs text-gray-500 mt-1">or click to browse</p>
            <p className="text-xs text-gray-400 mt-2">Supports: JPG, PNG, PDF (Max 10MB each)</p>
          </label>
        </div>

        {files.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                <span className="text-xs text-gray-400 mx-2">{(file.size / 1024).toFixed(1)} KB</span>
                <button onClick={() => removeFile(idx)} className="text-red-500 hover:text-red-700 ml-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {uploading && (
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex justify-between text-sm text-blue-700 mb-1">
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          </div>
        )}

        {uploadedUrls.length > 0 && (
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-sm text-green-700 font-medium">✓ {uploadedUrls.length} files uploaded successfully</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 p-3 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={handleClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button 
            onClick={handleUpload} 
            disabled={files.length === 0 || uploading}
            className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} Files`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Custom Category Modal
const CustomCategoryModal = ({ isOpen, onClose, customCategories, onSave }) => {
  const [newCategory, setNewCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      onSave({ ...customCategories, [newCategory]: [] });
      setNewCategory('');
    }
  };

  const handleAddSubCategory = () => {
    if (selectedCategory && newSubCategory.trim()) {
      const updated = {
        ...customCategories,
        [selectedCategory]: [...(customCategories[selectedCategory] || []), newSubCategory]
      };
      onSave(updated);
      setNewSubCategory('');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Custom Categories">
      <div className="space-y-6">
        {/* Add New Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Add New Category</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="e.g. Marketing Expenses"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button
              onClick={handleAddCategory}
              className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 text-sm"
            >
              Add
            </button>
          </div>
        </div>

        {/* Add Sub-Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Add Sub-Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
          >
            <option value="">Select Category</option>
            {Object.keys(customCategories).map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          {selectedCategory && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
                placeholder="e.g. Office Supplies"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                onClick={handleAddSubCategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Add
              </button>
            </div>
          )}
        </div>

        {/* Current Categories */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Custom Categories</label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {Object.entries(customCategories).map(([cat, subs]) => (
              <div key={cat} className="bg-gray-50 p-2 rounded">
                <p className="font-medium text-sm text-gray-700">{cat}</p>
                {subs.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{subs.join(', ')}</p>
                )}
              </div>
            ))}
            {Object.keys(customCategories).length === 0 && (
              <p className="text-sm text-gray-400">No custom categories yet</p>
            )}
          </div>
        </div>

        <button onClick={onClose} className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
          Done
        </button>
      </div>
    </Modal>
  );
};

const ExpenseForm = ({ onSubmit, onClose, initialData, allCategories, allSubCategories }) => {
  const [formData, setFormData] = useState(initialData || {
    date: getTodayDate(),
    expense_category: 'General & Admin Expenses',
    sub_category: '',
    ib_channel: '',
    amount: '',
    payment_from: 'Main Account',
    remark: '',
    attachment_url: '',
    attachments: []
  });
  const [uploading, setUploading] = useState(false);
  const [showCustomSubCat, setShowCustomSubCat] = useState(false);
  const [customSubCat, setCustomSubCat] = useState('');
  
  // Custom sub-categories storage
  const [customSubCategories, setCustomSubCategories] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ua_custom_subcategories')) || {};
    } catch { return {}; }
  });

  const baseSubCategories = allSubCategories[formData.expense_category] || [];
  const customSubs = customSubCategories[formData.expense_category] || [];
  const currentSubCategories = [...baseSubCategories, ...customSubs.filter(s => !baseSubCategories.includes(s))];
  const isAdvertising = formData.expense_category === 'Advertising';
  
  const handleAddCustomSubCat = () => {
    if (customSubCat.trim() && !currentSubCategories.includes(customSubCat.trim())) {
      const category = formData.expense_category;
      const newCustomSubs = {
        ...customSubCategories,
        [category]: [...(customSubCategories[category] || []), customSubCat.trim()]
      };
      setCustomSubCategories(newCustomSubs);
      localStorage.setItem('ua_custom_subcategories', JSON.stringify(newCustomSubs));
      setFormData({...formData, sub_category: customSubCat.trim()});
      setCustomSubCat('');
      setShowCustomSubCat(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ 
        ...formData, 
        attachment_url: response.data.url,
        attachments: [...(formData.attachments || []), { name: file.name, url: response.data.url }]
      });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...formData, amount: parseFloat(formData.amount) });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
        <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select value={formData.expense_category} 
            onChange={(e) => setFormData({...formData, expense_category: e.target.value, sub_category: '', ib_channel: ''})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
            {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category</label>
          {showCustomSubCat ? (
            <div className="flex gap-2">
              <input type="text" value={customSubCat} onChange={(e) => setCustomSubCat(e.target.value)}
                placeholder="Enter custom sub-category"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
              <button type="button" onClick={handleAddCustomSubCat} className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Plus className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setShowCustomSubCat(false)} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select value={formData.sub_category} onChange={(e) => setFormData({...formData, sub_category: e.target.value})}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required>
                <option value="">-- Select --</option>
                {currentSubCategories.map(sub => <option key={sub} value={sub}>{sub}</option>)}
              </select>
              <button type="button" onClick={() => setShowCustomSubCat(true)} 
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" title="Add custom sub-category">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* IB Channel for Advertising */}
      {isAdvertising && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">IB Channel</label>
          <select value={formData.ib_channel} onChange={(e) => setFormData({...formData, ib_channel: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
            <option value="">-- Select IB Channel --</option>
            {IB_CHANNELS.map(ch => <option key={ch} value={ch}>{ch}</option>)}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount (RM)</label>
        <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment From</label>
        <select value={formData.payment_from} onChange={(e) => setFormData({...formData, payment_from: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
          {PAYMENT_FROM.map(pf => <option key={pf} value={pf}>{pf}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
        <textarea value={formData.remark} onChange={(e) => setFormData({...formData, remark: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" rows={2} />
      </div>
      
      {/* Attachment Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Attachment</label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          {formData.attachments && formData.attachments.length > 0 ? (
            <div className="space-y-2">
              {formData.attachments.map((att, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <span className="text-sm text-gray-600 truncate">{att.name}</span>
                  <button type="button" onClick={() => setFormData({
                    ...formData, 
                    attachments: formData.attachments.filter((_, i) => i !== idx)
                  })} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <label className="cursor-pointer inline-block mt-2">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" />
                <span className="text-sm text-red-600 hover:text-red-700">+ Add more</span>
              </label>
            </div>
          ) : (
            <label className="cursor-pointer">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" />
              <div className="flex flex-col items-center gap-2">
                <Upload className={`w-8 h-8 ${uploading ? 'text-gray-400 animate-pulse' : 'text-gray-400'}`} />
                <span className="text-sm text-gray-500">{uploading ? 'Uploading...' : 'Click to upload (PDF/Image)'}</span>
              </div>
            </label>
          )}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={uploading} className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>
    </form>
  );
};

const ExpensesPage = () => {
  const { expenseList, addExpense, updateExpense, deleteExpense, loading } = useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Custom categories from localStorage
  const [customCategories, setCustomCategories] = useState(() => {
    const saved = localStorage.getItem('customExpenseCategories');
    return saved ? JSON.parse(saved) : {};
  });

  // Filter states
  const [filterType, setFilterType] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState(getMonthStart());
  const [customDateTo, setCustomDateTo] = useState(getTodayDate());
  const [filterCategory, setFilterCategory] = useState('');

  // Merge default and custom categories
  const allCategories = [...DEFAULT_EXPENSE_CATEGORIES, ...Object.keys(customCategories)];
  const allSubCategories = { ...DEFAULT_SUB_CATEGORIES, ...customCategories };

  // Save custom categories
  const handleSaveCustomCategories = (categories) => {
    setCustomCategories(categories);
    localStorage.setItem('customExpenseCategories', JSON.stringify(categories));
  };

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/expenses/analytics?period=${period}`);
        setAnalyticsData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setAnalyticsLoading(false);
      }
    };
    fetchAnalytics();
  }, [period, expenseList]);

  // Ensure arrays
  const safeExpenseList = Array.isArray(expenseList) ? expenseList : [];

  // Filter expense list
  const filteredExpenses = safeExpenseList.filter(item => {
    if (filterType === 'week') {
      const weekStart = getWeekStart();
      const weekEnd = getWeekEnd();
      if (item.date < weekStart || item.date > weekEnd) return false;
    } else if (filterType === 'month') {
      const monthStart = getMonthStart();
      if (!item.date.startsWith(monthStart.substring(0, 7))) return false;
    } else if (filterType === 'custom') {
      if (item.date < customDateFrom || item.date > customDateTo) return false;
    }
    if (filterCategory && item.expense_category !== filterCategory) return false;
    return true;
  });

  // Prepare chart data
  const chartData = analyticsData?.data?.map(d => ({
    period: d.period,
    amount: d.total
  })) || [];

  // Prepare pie chart data by category
  const byCategoryData = safeExpenseList.reduce((acc, item) => {
    const cat = item.expense_category || 'Other';
    const existing = acc.find(a => a.name === cat);
    if (existing) existing.value += item.amount || 0;
    else acc.push({ name: cat, value: item.amount || 0 });
    return acc;
  }, []).sort((a, b) => b.value - a.value);

  const handleAdd = async (data) => {
    await addExpense(data);
    setModalOpen(false);
  };

  const handleEdit = async (data) => {
    await updateExpense(editingItem.id, data);
    setEditingItem(null);
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this expense entry?')) {
      await deleteExpense(id);
    }
  };

  const handleBulkUploadComplete = (urls) => {
    console.log('Uploaded files:', urls);
    setBulkUploadOpen(false);
  };

  const filteredTotal = filteredExpenses.reduce((sum, item) => sum + (item.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Expenses</h1>
          <p className="text-gray-500 text-sm">Manage and track company expenditure</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Period Selector */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
            {['monthly', 'quarterly', 'yearly'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === p ? 'bg-red-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {p === 'monthly' ? 'Monthly' : p === 'quarterly' ? 'Quarterly' : 'Yearly'}
              </button>
            ))}
          </div>
          <button onClick={() => setCategoryModalOpen(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Settings className="w-4 h-4" /> Categories
          </button>
          <button onClick={() => setBulkUploadOpen(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Images className="w-4 h-4" /> Bulk Upload
          </button>
          <CSVExportButton endpoint="/export/expenses.csv" filename="expenses" testId="export-expenses-csv" />
          <button onClick={() => { setEditingItem(null); setModalOpen(true); }}
            className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Expense
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>
          
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            {[
              { value: 'all', label: 'All' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'custom', label: 'Custom' }
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilterType(f.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filterType === f.value ? 'bg-white text-red-700 shadow-sm' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {filterType === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
              <span className="text-gray-500">to</span>
              <input type="date" value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </div>
          )}

          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
            <option value="">All Categories</option>
            {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-5 h-5 text-red-600" />
            <span className="text-gray-500 text-sm">Total Expenses</span>
          </div>
          <p className="text-xl font-bold text-red-600">
            {formatRM(analyticsData?.summary?.total || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <span className="text-gray-500 text-sm">Filtered Total</span>
          </div>
          <p className="text-xl font-bold text-blue-600">
            {formatRM(filteredTotal)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            <span className="text-gray-500 text-sm">Entries</span>
          </div>
          <p className="text-xl font-bold text-purple-600">
            {filteredExpenses.length} / {safeExpenseList.length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-5 h-5 text-amber-600" />
            <span className="text-gray-500 text-sm">Top Category</span>
          </div>
          <p className="text-lg font-bold text-amber-600 truncate">
            {byCategoryData.length > 0 ? byCategoryData[0]?.name : '-'}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Expense Trend</h3>
          {analyticsLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => formatRM(v)} />
                <Area type="monotone" dataKey="amount" stroke="#C41E3A" fill="#C41E3A" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Expenses by Category</h3>
          {byCategoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={byCategoryData} cx="50%" cy="50%" labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80} fill="#8884d8" dataKey="value">
                  {byCategoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatRM(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              No category data available
            </div>
          )}
        </div>
      </div>

      {/* Expense Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Expense Records</h3>
          <span className="text-sm text-gray-500">{filteredExpenses.length} entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Sub-Category</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Payment From</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredExpenses.slice(0, 50).map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{item.date}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.expense_category}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                      {item.sub_category || '-'}
                    </span>
                    {item.ib_channel && (
                      <span className="ml-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                        {item.ib_channel}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">
                    {formatRM(item.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.payment_from}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => { setEditingItem(item); setModalOpen(true); }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(item.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredExpenses.length === 0 && (
            <div className="p-8 text-center text-gray-400">No expense records found</div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingItem(null); }} 
        title={editingItem ? 'Edit Expense' : 'Add Expense'}>
        <ExpenseForm 
          onSubmit={editingItem ? handleEdit : handleAdd} 
          onClose={() => { setModalOpen(false); setEditingItem(null); }}
          initialData={editingItem}
          allCategories={allCategories}
          allSubCategories={allSubCategories}
        />
      </Modal>

      {/* Bulk Upload Modal */}
      <BulkUploadModal 
        isOpen={bulkUploadOpen} 
        onClose={() => setBulkUploadOpen(false)}
        onUploadComplete={handleBulkUploadComplete}
      />

      {/* Custom Category Modal */}
      <CustomCategoryModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        customCategories={customCategories}
        onSave={handleSaveCustomCategories}
      />
    </div>
  );
};

export default ExpensesPage;
